import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';


import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ARROW_LEFT from '../../../../assets/images/back.svg';

import { useContext, useEffect, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import RadiusButton from '../../../../components/RadiusButton';
import { getExamSummary } from '../../../../services/masterService';


export const ExamSummary = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const projectName = params?.projectName;

    const { projectDetails } = useContext(ProjectContext);
    const [drive, setDrive] = useState('Drive Name');
    const [course, setCourse] = useState('Course Name');
    const [batch, setBatch] = useState('Batch Name');
    const [examDate, setExamDate] = useState('DD-MM-YYYY');
    const [servers, setServers] = useState([]);
    const [selectedProjectMasterId, setSelectedProjectMasterId] = useState(null);

    const columns = [
        { id: 'serverId', label: 'Server Id', minWidth: 140 },
        { id: 'examHallName', label: 'Exam Hall Name', minWidth: 140 },
        { id: 'candidatesDataDownloaded', label: 'Candidate Downloaded', minWidth: 140 },
        { id: 'qPacksDownloaded', label: 'Qpacks Downloaded', minWidth: 140 },
        { id: 'candidateCount', label: 'Candidates Count', minWidth: 140 },
        { id: 'loggedIn', label: 'Logged In', minWidth: 140 },
        { id: 'examStarted', label: 'Exam Started', minWidth: 140},
        { id: 'examCompleted', label: 'Exam Completed', minWidth: 140 },
        { id: 'rPacksUploaded', label: 'Rpacks Uploaded', minWidth: 140 },
        { id: 'auditLogUploaded', label: 'Audit Logs Uploaded', minWidth: 140 },
        { id: 'firstLogin', label: 'First Login', minWidth: 140 },
        { id: 'lastLogin', label: 'Last Login', minWidth: 140 },
    ];

    useEffect(() => {
        const fetchExamSummary = async () => {
            try {
                const res = await getExamSummary(selectedProjectMasterId);
                if (res.code === 'OK') {
                    const projectMaster = res.data.projectMaster;
                    setDrive(projectMaster?.['drive.driveName']);
                    setCourse(projectMaster?.['course.courseName']);
                    setBatch(projectMaster?.['batch.batchName']);
                    setExamDate(projectMaster?.['examDate']);

                    const examHalls = res.data.examHalls;
                    const servers = res.data.servers.map((server) => {
                        return {
                            ...server,
                            examHalls: [],
                        }
                    });
                    for (let candidate of res.data.candidates) {
                        const server = servers.find((server) => server.id === candidate.serverId);
                        const examHall = examHalls.find((eHall) => eHall.id === candidate.examHallId);
                        if (server && examHall) {
                            const existingExamHall = server.examHalls.find((eHall) => eHall.id === examHall.id);
                            if (!existingExamHall) {
                                server.examHalls.push({
                                    ...examHall,
                                    candidates: [candidate],
                                    loggedInCandidatesCount: candidate.loggedInCount > 0 ? 1 : 0,
                                    examStartedCandidatesCount: candidate?.['examConfig.candidateExamStartTime'] ? 1 : 0,
                                    examCompletedCandidatesCount: candidate?.['examConfig.candidateExamEndTime'] ? 1 : 0,
                                    rPacksUploadedCount: candidate.rPacksUploaded ? 1 : 0,
                                    auditLogUploadedCount: candidate.logUploaded ? 1 : 0,
                                    firstLogin: candidate['loginTime'] ? dayjs.utc(candidate['loginTime']) : null,
                                    lastLogin: candidate['loginTime'] ? dayjs.utc(candidate['loginTime']) : null
                                });
                            } else {
                                if (candidate.loggedInCount > 0) {
                                    existingExamHall.loggedInCandidatesCount++;
                                }
                                if (candidate?.['examConfig.candidateExamStartTime']) {
                                    existingExamHall.examStartedCandidatesCount++;
                                }
                                if (candidate?.['examConfig.candidateExamEndTime']) {
                                    existingExamHall.examCompletedCandidatesCount++;
                                }
                                if (candidate.rPacksUploaded) {
                                    existingExamHall.rPacksUploadedCount++;
                                }
                                if (candidate.logUploaded) {
                                    existingExamHall.auditLogUploadedCount++;
                                }
                                if (candidate.loginTime) {
                                    const loginTime = dayjs.utc(candidate['loginTime']);
                                    if (!existingExamHall.firstLogin) {
                                        existingExamHall.firstLogin = loginTime;
                                    } else if (loginTime.isBefore(existingExamHall.firstLogin)) {
                                        existingExamHall.firstLogin = loginTime;
                                    }
                                    if (!existingExamHall.lastLogin) {
                                        existingExamHall.lastLogin = loginTime;
                                    } else if (loginTime.isAfter(existingExamHall.lastLogin)) {
                                        existingExamHall.lastLogin = loginTime;
                                    }
                                }
                                existingExamHall.candidates.push(candidate);
                            }
                        }
                    }

                    setServers([...servers]);
                }


            } catch (error) {
                console.log('There is an error while fetching exam summary', error);
            }
        }
        if (selectedProjectMasterId) {
            fetchExamSummary();
        }
    }, [selectedProjectMasterId])

    const onGoToDashboard = () => {
        if (projectName) {
            navigate(`/${projectName}/dashboard`);
        }
    }
    const onGoToServerSummary = (serverIndex, examHallIndex) => {
        const server = servers[serverIndex];
        const examHall = server.examHalls[examHallIndex];
        if (server?.id && examHall?.id) {
            navigate(`/${projectName}/server-summary`, {
                state: {
                    projectMasterId: selectedProjectMasterId,
                    serverId: server.id,
                    serverName: server.serverId,
                    examHallId: examHall.id,
                    examHallName: examHall.hallName
                }
            });
        }
    }

    useEffect(() => {
        const projectMasterId = location?.state?.projectMasterId;
        if (projectMasterId) {
            setSelectedProjectMasterId(projectMasterId);
        }
    }, [location]);

    const renderTableCell = (columnId, serverIndex, examHallIndex) => {
        const server = servers[serverIndex];
        const examHall = server.examHalls[examHallIndex];
        switch (columnId) {
            case 'serverId':
                return server['serverId']
            case 'examHallName':
                return examHall?.['hallName']
            case 'candidatesDataDownloaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: server?.['candidatesDataDownloaded'] ? 'success.main' : 'error.main',
                }}>
                    {server?.['candidatesDataDownloaded'] ? 'Yes' : 'No'}
                </Box>
            case 'qPacksDownloaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: server?.['qPacksDownloaded'] ? 'success.main' : 'error.main',
                }}>
                    {server?.['qPacksDownloaded'] ? 'Yes' : 'No'}
                </Box>
            case 'candidateCount':
                return examHall?.candidates?.length
            case 'loggedIn':
                return examHall?.['loggedInCandidatesCount']
            case 'examStarted':
                return examHall?.['examStartedCandidatesCount']
            case 'examCompleted':
                return examHall?.['examCompletedCandidatesCount']
            case 'rPacksUploaded':
                return examHall?.['rPacksUploadedCount']
            case 'auditLogUploaded':
                return examHall?.['auditLogUploadedCount']
            case 'firstLogin':
                return examHall?.['firstLogin']
                    ? examHall.firstLogin.format('DD-MM-YYYY HH:mm:ss')
                    : null;
            case 'lastLogin':
                return examHall?.['lastLogin']
                    ? examHall.lastLogin.format('DD-MM-YYYY HH:mm:ss')
                    : null;
            default:
                return null;
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Exam Summary</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>
                <h1 className='title'>Exam Summary</h1>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                    mb: 3
                }}>
                    <TextField
                        size='small'
                        label="Drive Name"
                        variant="outlined"
                        value={drive}
                        disabled
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            width: 220,
                            bgcolor: 'background.base'
                        }}
                    />
                    <TextField
                        size='small'
                        label="Course Name"
                        variant="outlined"
                        value={course}
                        disabled
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{
                            width: 220,
                            bgcolor: 'background.base'
                        }}
                    />
                    <TextField
                        size='small'
                        label="Batch Name"
                        variant="outlined"
                        value={batch}
                        disabled
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{
                            width: 220,
                            bgcolor: 'background.base'
                        }}
                    />
                    <TextField
                        size='small'
                        label="Exam Date"
                        variant="outlined"
                        value={examDate}
                        disabled
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{
                            width: 220,
                            bgcolor: 'background.base'
                        }}
                    />
                    <RadiusButton sx={{
                        borderRadius: 40,
                        textTransform: 'capitalize',
                        ml: 'auto'
                    }} color="grey"
                        variant='contained'
                        size='large'
                        onClick={onGoToDashboard}
                        startIcon={<img src={ARROW_LEFT} alt='' />}
                    >Go back</RadiusButton>

                </Box>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{
                                                color: 'primary.main'
                                            }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!servers?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={11} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : servers.map((server, serverIndex) => {
                                        return server.examHalls.map((examHall, examHallIndex) => {
                                            return (
                                                <TableRow hover={true} sx={{ cursor: 'pointer' }} role="checkbox" key={examHallIndex} onClick={() => onGoToServerSummary(serverIndex, examHallIndex)}>
                                                    {columns.map((column, index) => {
                                                        return <TableCell key={column.id} align='center' sx={{
                                                            px: 1.5,
                                                            py: 0.75
                                                        }}>
                                                            {renderTableCell(column.id, serverIndex, examHallIndex)}
                                                        </TableCell>
                                                    })}
                                                </TableRow>
                                            );
                                        })
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Container>
        </MainContainer>
    );
}
